import queryString from 'query-string';
import retryAsyncPromise, { RetryOptions } from '../util/retryAsync';

interface AuthData {
  type: string;
  token?: string | null;
}

interface CachedResponse {
  expires: number;
  response: any;
}

export type Verb = 'get' | 'post' | 'put' | 'delete';

export type VerifyResponseFn = (fetchResponse: Response, response: Response | Blob | any, url: string, options: FetchOptions) => Promise<void>;

export type FetchApiHelperFn = (url: string, options: FetchOptions, api: FetchApi | FetchApiHelperFn) => any;

export interface FetchOptions {
  xsrf?: boolean;
  signal?: AbortSignal;
  abortController?: AbortController;
  auth?: AuthData;
  authHeader?: string;
  baseurl?: string;
  query?: object;
  headers?: Record<string, string>;
  method?: string;
  data?: object;
  formdata?: Record<string, any>;
  body?: string | FormData;
  json?: boolean;
  file?: boolean;
  retry?: RetryOptions;
  verifyResponse?: VerifyResponseFn;
  return_url?: boolean;
  cache?: Record<string, Record<string, CachedResponse>>;
  useCache?: boolean | ((url: string, options: FetchOptions) => boolean);
  resetCache?: boolean;
  cacheLifetime?: number;
}

export interface FetchApi {
  (url: string, options?: FetchOptions): Promise<any>;
  resetCache: () => void;
  configure: (config: { defaults?: FetchOptions; verbs?: Verb[]; helpers?: Record<string, FetchApiHelperFn> }) => FetchApi;
  [key: string]: any;
}

/** Fetch enchancing function for calling apis.
 * @param {String} url - url to get resource.
 * @param {Object} options - options object used in fetch.
 * @param {Object} options.auth - data to add to authentication header
 * @param {Object} options.auth.type - authentication type
 * @param {Object} options.auth.token - authentication token
 * @param {string} options.baseurl - base url to prepend to given url.
 * @param {Object} options.query - object to append to url as query parameters.
 * @param {Object} options.headers - headers to add to the request
 * @param {string} options.method - method to use in request.
 * @param {Object} options.data - object to stringify into an application/json payload for the request
 * @param {Object} options.formdata - object of entries encode into an application/x-www-form-urlencoded encoded data payload for the request.
 * @param {string} options.body - raw text data to send with request.
 * @param {boolean} options.json - wether this request returns parsed json as a response or just a fetch request response.
 * @param {Function} options.verifyResponse - function to call to verify the obtained response.
 * @param {boolean} options.return_url - Wether to return the request's url instead of calling the request.
 * @param {Object} options.cache - cache object to use for setting/getting data from cache.
 * @param {boolean} options.useCache - wether to use the given cache for setting/getting cached data.
 * @param {boolean} options.resetCache - wether to ignore any data already in the given cache and to reset it with the results.
 * @returns {Promise} resolved returned by fetch().
 */
async function __fetchApi(url: string, _options: FetchOptions): Promise<any> {
  const options = _options || {};
  options.headers = options.headers || {};

  if (options.query) {
    const prefix = url.indexOf('?') === -1 ? '?' : '&';
    url = url + prefix + queryString.stringify(options.query);
    delete options.query;
  }

  if (options.baseurl) {
    url = `${options.baseurl}${url}`;
    delete options.baseurl;
  }

  if (options.return_url) {
    return url;
  }

  if (options.formdata) {
    const fd = new FormData();
    Object.entries(options.formdata).forEach(([name, value]) => {
      if (value && value.file && value.filename) {
        fd.append(name, value.file, value.filename);
      } else {
        fd.append(name, value);
      }
    });

    options.body = fd;
    delete options.formdata;
  } else if (options.data) {
    options.body = JSON.stringify(options.data);
    options.headers['Content-Type'] = 'application/json';
    delete options.data;
  }

  if (options.auth) {
    const authHeader = options.authHeader || 'Authorization';
    options.headers[authHeader] = `${options.auth.type} ${options.auth.token}`;
    delete options.auth;
  }

  const { json, file, cache, useCache: useCacheOption, resetCache, cacheLifetime, verifyResponse, retry: retryOptions, abortController } = options;

  if (abortController && !options.signal) {
    options.signal = abortController.signal;
  }

  const useCache = typeof useCacheOption === 'function' ? useCacheOption(url, options) : useCacheOption;
  delete options.json;
  delete options.cache;
  delete options.useCache;
  delete options.cacheLifetime;
  delete options.resetCache;
  delete options.retry;
  delete options.verifyResponse;
  const method = (options.method || 'get').toLowerCase();
  const now = new Date().getTime();

  if (cache && useCache) {
    const cachedResponse = (cache[method] || {})[url];

    if (cachedResponse && !resetCache) {
      if (cachedResponse.expires < now) {
        delete cache[method][url];
      } else {
        return Promise.resolve(cachedResponse.response);
      }
    }
  }

  return retryAsyncPromise(async () => {
    const fetchResponse = await fetch(url, options as RequestInit);
    let response: Response | Blob | any = fetchResponse;

    if (json) {
      response = await response.json();
    } else if (file) {
      response = await response.blob();
    }

    if (verifyResponse && typeof verifyResponse === 'function') await verifyResponse(fetchResponse, response, url, options);

    if (useCache && cache) {
      (cache[method] || (cache[method] = {}))[url] = {
        expires: now + cacheLifetime!,
        response,
      };
    }

    return response;
  }, retryOptions);
}

__fetchApi.cache = {};

const fetchApi: FetchApi = (url, options) =>
  __fetchApi(url, {
    cache: __fetchApi.cache,
    ...fetchApi.DEFAULT_OPTIONS,
    ...options,
  });

fetchApi.cache = {};
fetchApi.resetCache = () => {
  fetchApi.cache = {};
};

fetchApi.configure = function fetchApi_configure({ defaults, verbs = ['get', 'post', 'put', 'delete'], helpers }) {
  fetchApi.DEFAULT_OPTIONS = defaults || {};
  let { baseurl } = fetchApi.DEFAULT_OPTIONS;

  if (baseurl && baseurl[baseurl.length - 1] !== '/') {
    baseurl += '/';
  }

  fetchApi.DEFAULT_OPTIONS.baseurl = baseurl;

  [[], ...Object.entries(helpers || {})].forEach(([helperName, helperFn]) => {
    const helper: any = (url: string, options: FetchOptions) => {
      return helperFn ? helperFn(url, options, fetchApi) : fetchApi(url, options);
    };

    if (helperName) {
      fetchApi[helperName] = helper;
    }

    helper.url = (url: string) => helper(url, { return_url: true });

    verbs.forEach(verb => {
      helper[verb] = (url: string, options: FetchOptions) => helper(url, { method: verb, ...options });
    });
  });

  fetchApi.configured = true;

  return fetchApi;
};

export default fetchApi;
